// Публикуване на обява за имот — минава през одобрение от модератор.
// С listingId в параметрите: РЕДАКЦИЯ/ПОДНОВЯВАНЕ на съществуваща обява
// (архивирана/отхвърлена/чернова) — пази историята, не създава нова.

import React, { useEffect, useState } from 'react';
import { Image, Pressable, Text, View } from 'react-native';
import { api, API_URL } from '../../api';
import { pickAndUploadPhotos } from '../../photo-picker';
import { showError, showSuccess } from '../../toast';
import { Button, Card, COLORS, Input, Label, Muted, Screen, Title } from '../../ui';
import { t } from '../../i18n';

export default function CreateListingScreen({ route, navigation }: any) {
  const { apartmentId, defaultPriceCents, listingId } = route.params;
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState(defaultPriceCents ? String(defaultPriceCents / 100) : '');
  const [photos, setPhotos] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (listingId) {
      // редакция: зареждаме самата обява
      api('/listings/mine')
        .then((r) => {
          const l = r.listings.find((x: any) => x.id === listingId);
          if (!l) return;
          setTitle(l.title);
          setDescription(l.description);
          setPrice(String(l.priceCents / 100));
          setPhotos(l.photos ?? []);
        })
        .catch(() => {});
    } else {
      // нова: снимките на имота като начални (могат да се добавят още)
      api(`/apartments/${apartmentId}`)
        .then((r) => setPhotos(r.apartment?.photos ?? []))
        .catch(() => {});
    }
  }, [apartmentId, listingId]);

  const addPhoto = async () => {
    // избор на НЯКОЛКО снимки наведнъж — качват се паралелно
    const urls = await pickAndUploadPhotos();
    if (urls.length) setPhotos((p) => [...p, ...urls]);
  };

  const removePhoto = (url: string) => setPhotos((p) => p.filter((x) => x !== url));

  const submit = async () => {
    setLoading(true);
    try {
      const fields = {
        title,
        description,
        priceCents: Math.round(Number(price.replace(',', '.')) * 100),
        photos,
      };
      if (listingId) {
        // редакция + повторно изпращане на СЪЩАТА обява
        await api(`/listings/${listingId}`, { method: 'PATCH', body: fields });
        await api(`/listings/${listingId}/submit`, { body: {} });
      } else {
        await api('/listings', { body: { apartmentId, ...fields } });
      }
      showSuccess(t('Обявата е изпратена за одобрение от модератор.'));
      navigation.goBack();
    } catch (e: any) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Screen>
      <Title>{listingId ? t('Редакция на обява') : t('Нова обява')}</Title>
      <Card>
        <Muted>
          {listingId
            ? t('Промените минават през ново одобрение от модератор, но обявата и чатовете ѝ се запазват.')
            : t('Градът се взима от имота. Обявата се преглежда от модератор преди публикуване.')}
        </Muted>
        <Label>{t('Заглавие')}</Label>
        <Input value={title} onChangeText={setTitle} placeholder={t('Светъл двустаен до метрото')} />
        <Label>{t('Описание')}</Label>
        <Input
          value={description}
          onChangeText={setDescription}
          multiline
          placeholder={t('Обзаведен, южно изложение, до парка…')}
          style={{ minHeight: 100, textAlignVertical: 'top' }}
        />
        <Label>{t('Месечен наем (€)')}</Label>
        <Input value={price} onChangeText={setPrice} keyboardType="decimal-pad" placeholder="450" />

        <Label>{t('Снимки ({n})', { n: photos.length })}</Label>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          {photos.map((p) => (
            <View key={p}>
              <Image source={{ uri: `${API_URL}${p}` }} style={{ width: 90, height: 90, borderRadius: 8 }} />
              <Pressable
                onPress={() => removePhoto(p)}
                style={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  width: 24,
                  height: 24,
                  borderRadius: 12,
                  backgroundColor: COLORS.danger,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Text style={{ color: '#fff', fontWeight: '700' }}>✕</Text>
              </Pressable>
            </View>
          ))}
        </View>
        <Button title={t('+ Добави снимка')} variant="secondary" onPress={addPhoto} />
        {photos.length === 0 ? (
          <Muted>{t('📸 Обявите със снимки получават повече запитвания — добавете поне една.')}</Muted>
        ) : (
          <Muted>{t('Снимките се показват в обявата и на картата в началния екран.')}</Muted>
        )}

        <Button
          title={listingId ? t('Запази и изпрати за одобрение') : t('Изпрати за одобрение')}
          onPress={submit}
          disabled={!title || !description || !price}
          loading={loading}
        />
      </Card>
    </Screen>
  );
}
